import { useEffect, useRef, useState } from 'react'

interface CountUpProps {
  value: number
  duration?: number
  format?: (value: number) => string
  className?: string
}

export function CountUp({
  value,
  duration = 600,
  format,
  className
}: CountUpProps) {
  const [displayValue, setDisplayValue] = useState(value)
  const fromRef = useRef(value)
  const startRef = useRef(performance.now())
  const rafRef = useRef<number>(0)

  useEffect(() => {
    if (Number.isNaN(value)) return
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (reduceMotion || duration <= 0 || displayValue === value) {
      setDisplayValue(value)
      return
    }
    fromRef.current = displayValue
    startRef.current = performance.now()
    const tick = (now: number) => {
      const elapsed = now - startRef.current
      const progress = Math.min(1, elapsed / duration)
      const eased = 1 - (1 - progress) ** 3
      const next = fromRef.current + (value - fromRef.current) * eased
      setDisplayValue(progress >= 1 ? value : next)
      if (progress < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration])

  const formatted = format
    ? format(displayValue)
    : Math.round(displayValue).toLocaleString()
  return <span className={className}>{formatted}</span>
}
